import { useId, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/toast';
import { TrustBar } from '../conversion/TrustBar';
import { readAttribution } from '../../lib/tracking';
import { useExperiment } from '../../lib/analytics';
import { EXPERIMENTS } from '../../config/experiments';

const schema = z.object({
  firstName: z.string().min(1, 'Prénom requis').max(60).trim(),
  lastName: z.string().min(1, 'Nom requis').max(60).trim(),
  email: z.string().email('Adresse e-mail invalide').trim(),
  phone: z
    .string()
    .trim()
    .max(30, 'Numéro de téléphone invalide')
    .refine((v) => v === '' || v.replace(/\D/g, '').length >= 10, 'Numéro de téléphone invalide')
    .optional(),
  message: z.string().max(2000, 'Message limité à 2000 caractères').trim().optional(),
});

type FormKey = keyof z.infer<typeof schema>;

interface ApiError {
  error?: string;
}

export interface LeadFormProps {
  title: string;
  description?: string;
  source: 'contact' | 'newsletter' | 'form';
  segment?: 'pro' | 'entreprise' | 'particulier' | 'syndic' | 'asso' | 'agri' | 'inconnu';
  intent?: 'cee' | 'comparer' | 'renover' | 'conformite' | 'inconnu';
}

export function LeadForm({
  title,
  description,
  source,
  segment = 'inconnu',
  intent = 'inconnu',
}: LeadFormProps) {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<Partial<Record<FormKey, string>>>({});
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [success, setSuccess] = useState(false);
  const formId = useId();
  const { variant: trustVariant } = useExperiment<'badges' | 'control'>(
    EXPERIMENTS.trustBadges.key,
    'badges',
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;

    const form = e.currentTarget;
    const data = new FormData(form);

    const candidate = {
      firstName: String(data.get('firstName') ?? ''),
      lastName: String(data.get('lastName') ?? ''),
      email: String(data.get('email') ?? ''),
      phone: String(data.get('phone') ?? '') || undefined,
      message: String(data.get('message') ?? '') || undefined,
    };

    const result = schema.safeParse(candidate);
    if (!result.success) {
      const errs: Partial<Record<FormKey, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as FormKey | undefined;
        if (key) errs[key] = issue.message;
      }
      setIssues(errs);
      return;
    }
    setIssues({});

    const payload = {
      ...result.data,
      consentMarketing,
      source,
      segment,
      intent,
      ...readAttribution(),
    };

    setLoading(true);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as ApiError;
        throw new Error(body.error ?? `Erreur ${res.status}`);
      }

      setSuccess(true);
      form.reset();
      setConsentMarketing(false);
      toast.success('Demande envoyée !', {
        description: 'Notre équipe vous répond sous 24 h ouvrées.',
      });
    } catch (err) {
      toast.error('Envoi impossible', {
        description: err instanceof Error ? err.message : 'Réessayez dans un instant.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-stone-50 py-10 md:py-16">
      <Container className="max-w-3xl">
        <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-soft sm:p-6 md:p-8">
          <h2 className="text-h-section tracking-tightish">{title}</h2>
          {description ? <p className="mt-2 text-stone-600">{description}</p> : null}

          {success ? (
            <div
              className="mt-6 rounded-xl border border-brand-200 bg-brand-50/50 p-5 text-center"
              role="status"
              aria-live="polite"
            >
              <p className="font-display text-[17px] font-semibold text-ink">Demande bien reçue</p>
              <p className="mt-2 text-[14px] leading-relaxed text-stone-600">
                Notre équipe vous répond sous 24 h ouvrées. Vous pouvez aussi explorer nos outils
                gratuits en attendant.
              </p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Button asChild variant="brand" size="md">
                  <a href="/outils">Voir nos outils</a>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={() => setSuccess(false)}
                >
                  Envoyer une autre demande
                </Button>
              </div>
              {trustVariant !== 'control' && (
                <div className="mt-4">
                  <TrustBar max={3} />
                </div>
              )}
            </div>
          ) : (
            <form
              className="mt-6 grid gap-4 sm:grid-cols-2"
              data-lead-source={source}
              onSubmit={handleSubmit}
              noValidate
            >
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${formId}-first-name`}>Prénom</Label>
                <Input
                  id={`${formId}-first-name`}
                  name="firstName"
                  autoComplete="given-name"
                  disabled={loading}
                  placeholder="Jean"
                  aria-invalid={!!issues.firstName}
                  aria-describedby={issues.firstName ? `${formId}-first-name-error` : undefined}
                  className="h-11 aria-[invalid=true]:border-red-400"
                />
                {issues.firstName && (
                  <p
                    id={`${formId}-first-name-error`}
                    className="text-[11.5px] font-medium text-red-600"
                    role="alert"
                  >
                    {issues.firstName}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor={`${formId}-last-name`}>Nom</Label>
                <Input
                  id={`${formId}-last-name`}
                  name="lastName"
                  autoComplete="family-name"
                  disabled={loading}
                  placeholder="Dupont"
                  aria-invalid={!!issues.lastName}
                  aria-describedby={issues.lastName ? `${formId}-last-name-error` : undefined}
                  className="h-11 aria-[invalid=true]:border-red-400"
                />
                {issues.lastName && (
                  <p
                    id={`${formId}-last-name-error`}
                    className="text-[11.5px] font-medium text-red-600"
                    role="alert"
                  >
                    {issues.lastName}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor={`${formId}-email`}>Email</Label>
                <Input
                  id={`${formId}-email`}
                  type="email"
                  name="email"
                  autoComplete="email"
                  disabled={loading}
                  placeholder="vous@email.com"
                  aria-invalid={!!issues.email}
                  aria-describedby={issues.email ? `${formId}-email-error` : undefined}
                  className="h-11 aria-[invalid=true]:border-red-400"
                />
                {issues.email && (
                  <p
                    id={`${formId}-email-error`}
                    className="text-[11.5px] font-medium text-red-600"
                    role="alert"
                  >
                    {issues.email}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor={`${formId}-phone`}>
                  Téléphone <span className="font-normal text-stone-400">(optionnel)</span>
                </Label>
                <Input
                  id={`${formId}-phone`}
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  disabled={loading}
                  placeholder="06 12 34 56 78"
                  aria-invalid={!!issues.phone}
                  aria-describedby={issues.phone ? `${formId}-phone-error` : undefined}
                  className="h-11 aria-[invalid=true]:border-red-400"
                />
                {issues.phone && (
                  <p
                    id={`${formId}-phone-error`}
                    className="text-[11.5px] font-medium text-red-600"
                    role="alert"
                  >
                    {issues.phone}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor={`${formId}-message`}>
                  Message <span className="font-normal text-stone-400">(optionnel)</span>
                </Label>
                <Textarea
                  id={`${formId}-message`}
                  name="message"
                  rows={4}
                  disabled={loading}
                  placeholder="Décrivez votre projet ou votre question…"
                  aria-invalid={!!issues.message}
                  aria-describedby={issues.message ? `${formId}-message-error` : undefined}
                  className="aria-[invalid=true]:border-red-400"
                />
                {issues.message && (
                  <p
                    id={`${formId}-message-error`}
                    className="text-[11.5px] font-medium text-red-600"
                    role="alert"
                  >
                    {issues.message}
                  </p>
                )}
              </div>

              <label className="flex cursor-pointer items-start gap-2 text-xs sm:col-span-2">
                <input
                  type="checkbox"
                  name="consentMarketing"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                  aria-label="Recevoir les actualités aides et primes de Solution Éco France"
                  className="mt-0.5 h-4 w-4 rounded border-stone-300 text-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/30"
                />
                <span className="text-stone-600">
                  Je souhaite recevoir les actualités aides &amp; primes de Solution Éco France.{' '}
                  <span className="text-stone-400">(Optionnel — désabonnement en 1 clic)</span>
                </span>
              </label>

              <Button
                type="submit"
                variant="brand"
                size="xl"
                disabled={loading}
                className="sm:col-span-2"
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Envoi en cours…
                  </>
                ) : (
                  'Envoyer ma demande'
                )}
              </Button>

              {trustVariant !== 'control' && (
                <div className="sm:col-span-2">
                  <TrustBar max={4} />
                </div>
              )}
            </form>
          )}
        </div>
      </Container>
    </section>
  );
}

export const LeadFormConfig: ComponentConfig<LeadFormProps> = {
  fields: {
    title: { type: 'text' },
    description: { type: 'textarea' },
    source: {
      type: 'select',
      options: [
        { label: 'Contact', value: 'contact' },
        { label: 'Newsletter', value: 'newsletter' },
        { label: 'Formulaire', value: 'form' },
      ],
    },
    segment: {
      type: 'select',
      options: [
        { label: 'Professionnel', value: 'pro' },
        { label: 'Entreprise', value: 'entreprise' },
        { label: 'Particulier', value: 'particulier' },
        { label: 'Syndic / Copropriété', value: 'syndic' },
        { label: 'Association', value: 'asso' },
        { label: 'Agriculteur', value: 'agri' },
        { label: 'Inconnu', value: 'inconnu' },
      ],
    },
    intent: {
      type: 'select',
      options: [
        { label: 'Primes CEE', value: 'cee' },
        { label: 'Comparateur énergie', value: 'comparer' },
        { label: 'Aides rénovation', value: 'renover' },
        { label: 'Décret Tertiaire', value: 'conformite' },
        { label: 'Inconnu', value: 'inconnu' },
      ],
    },
  },
  defaultProps: {
    title: 'Parlons de votre projet',
    description: 'Notre équipe répond sous 24 h ouvrées. Aucun engagement, aucune avance de frais.',
    source: 'contact',
    segment: 'inconnu',
    intent: 'inconnu',
  },
  render: LeadForm,
};
